/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import type { BackgroundOpacity, FontFamily, FontSize, TextColor, TextEdgeStyle } from "@lumi/player-core";
import React from "react";
import type { ReactElement } from "react";

// - Renders a <video> element
// - Attaches PlayerController and loads given HLS URL
// - Shows play/pause, a time/seek bar, quality + captions selects
// - Handles keyboard shortcuts (Space, ←/→, Shift+←/→, F, C)

import { preloadThumbnailSprite } from "../../../shared/hooks/useThumbnailSprite";
import type { AuthenticatedClient, User } from "../../../shared/types/auth";
import { usePlayerControllerSetup } from "../hooks/usePlayerControllerSetup";
import { usePlayerSettings } from "../hooks/usePlayerSettings";
import { usePlayerShortcuts } from "../hooks/usePlayerShortcuts";
import { OverlayChrome } from "./OverlayChrome";

export type PlayerDemoProps = Readonly<{
  src: string;
  subtitles?: ReadonlyArray<{ lang: string; url: string; forced?: boolean }>;
  onBack?: () => void;
  onEnded?: () => void;
  assetId: string;
  titleId: string;
  user?: User | null;
  authenticatedClient?: AuthenticatedClient | null;
  vttUrl?: string | null;
}>;

export function PlayerDemo({
  src,
  subtitles,
  onBack,
  onEnded,
  assetId,
  titleId,
  user,
  authenticatedClient,
  vttUrl,
}: PlayerDemoProps): ReactElement {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  // Settings persistence
  const { settings, updateSettings } = usePlayerSettings();
  const [subtitlePosition, setSubtitlePosition] = React.useState<"auto" | "top" | "middle" | "bottom">(
    settings.subtitlePosition,
  );
  const [subtitleFontSize, setSubtitleFontSize] = React.useState(settings.subtitleFontSize);
  const [subtitleBackgroundOpacity, setSubtitleBackgroundOpacity] = React.useState(settings.subtitleBackgroundOpacity);
  const [subtitleTextColor, setSubtitleTextColor] = React.useState(settings.subtitleTextColor);
  const [subtitleFontFamily, setSubtitleFontFamily] = React.useState(settings.subtitleFontFamily);
  const [subtitleTextEdge, setSubtitleTextEdge] = React.useState(settings.subtitleTextEdge);

  const {
    controllerRef,
    time,
    duration,
    levels,
    selectedLevel,
    setSelectedLevel,
    captions,
    activeCaption,
    setActiveCaption,
    playbackRate,
    setPlaybackRate,
  } = usePlayerControllerSetup({
    videoRef,
    src,
    subtitles,
    setIsPlaying,
    assetId,
    titleId,
    user,
    authenticatedClient: authenticatedClient ?? undefined,
  });

  // Apply persisted settings after controller loads
  React.useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl || levels.length === 0) return;

    // Apply persisted quality setting
    if (settings.quality !== "auto" && settings.quality >= 0) {
      const qualityExists = levels.some((level) => level.height === settings.quality);
      if (qualityExists) {
        ctrl.setLevel(settings.quality);
        setSelectedLevel(settings.quality);
      }
    }

    // Apply persisted playback speed
    if (settings.playbackSpeed !== 1) {
      ctrl.setPlaybackRate(settings.playbackSpeed);
      setPlaybackRate(settings.playbackSpeed);
    }

    // Apply persisted subtitle position
    if (settings.subtitlePosition !== "auto" && "setSubtitlePosition" in ctrl) {
      ctrl.setSubtitlePosition(settings.subtitlePosition);
      setSubtitlePosition(settings.subtitlePosition);
    }
  }, [controllerRef, levels, captions, settings, setSelectedLevel, setActiveCaption, setPlaybackRate]);

  // Apply persisted caption track after captions load
  React.useEffect(() => {
    const ctrl = controllerRef.current;
    if (!ctrl || captions.length === 0) return;

    if (settings.subtitlesEnabled && settings.selectedSubtitleId !== "off") {
      const captionExists = captions.some((cap) => cap.id === settings.selectedSubtitleId);
      if (captionExists) {
        // Clear redundancy guard to force application to newly loaded tracks
        // This is safe because we're only applying when captions array changes
        if ("_clearCaptionGuard" in ctrl && typeof ctrl._clearCaptionGuard === "function") {
          (ctrl as { _clearCaptionGuard: () => void })._clearCaptionGuard();
        }
        ctrl.setCaption(settings.selectedSubtitleId);
        setActiveCaption(settings.selectedSubtitleId);
      }
    } else if (!settings.subtitlesEnabled) {
      ctrl.setCaption("off");
      setActiveCaption("off");
    }
  }, [controllerRef, captions, settings.subtitlesEnabled, settings.selectedSubtitleId, setActiveCaption]);

  // Handle video ended event
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !onEnded) return;

    const handleEnded = () => {
      console.log("🎬 Video ended, calling onEnded callback");
      onEnded();
    };

    video.addEventListener("ended", handleEnded);
    return () => {
      video.removeEventListener("ended", handleEnded);
    };
  }, [videoRef, onEnded]);

  // Notify Electron to prevent screen saver during video playback
  React.useEffect(() => {
    globalThis.window?.electronAPI?.setVideoPlaying?.(isPlaying);

    // Cleanup: ensure power save blocker is released when component unmounts
    return () => {
      globalThis.window?.electronAPI?.setVideoPlaying?.(false);
    };
  }, [isPlaying]);

  // Preload thumbnail sprites after a short delay
  // This avoids competing with initial HLS segment buffering
  React.useEffect(() => {
    if (!vttUrl) return;

    const timer = setTimeout(() => {
      void preloadThumbnailSprite(vttUrl);
    }, 1000);

    return () => clearTimeout(timer);
  }, [vttUrl]);

  // Keyboard shortcuts
  usePlayerShortcuts({
    controllerRef,
    containerRef,
    videoRef,
    time,
    duration,
    setIsPlaying,
    setActiveCaption,
    setShowHUD: () => undefined,
    onBack,
  });

  const onTogglePlay = () => {
    const ctrl = controllerRef.current;
    const v = videoRef.current;
    if (!ctrl || !v) return;
    if (v.paused) {
      ctrl
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      ctrl
        .pause()
        .then(() => setIsPlaying(false))
        .catch(() => undefined);
    }
  };

  const onSeek = (nextSeconds: number) => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    ctrl.seek(nextSeconds);
  };

  const onChangeLevel = (parsed: number | "auto") => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    ctrl.setLevel(parsed);
    setSelectedLevel(parsed);
    updateSettings({ quality: parsed });
  };

  const onChangeCaption = (parsed: number | "off") => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    ctrl.setCaption(parsed);
    setActiveCaption(parsed);
    updateSettings({
      subtitlesEnabled: parsed !== "off",
      selectedSubtitleId: parsed,
    });
  };

  const onChangePlaybackRate = (rate: number) => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    ctrl.setPlaybackRate(rate);
    setPlaybackRate(rate);
    updateSettings({ playbackSpeed: rate });
  };

  const handleControlsVisibilityChange = (visible: boolean) => {
    const ctrl = controllerRef.current;
    if (!ctrl || !("setControlsVisible" in ctrl)) return;
    ctrl.setControlsVisible(visible);
  };

  const handleSubtitlePositionChange = (position: "auto" | "top" | "middle" | "bottom") => {
    const ctrl = controllerRef.current;
    if (!ctrl || !("setSubtitlePosition" in ctrl)) return;
    ctrl.setSubtitlePosition(position);
    setSubtitlePosition(position);
    updateSettings({ subtitlePosition: position });
  };

  const handleSubtitleFontSizeChange = (size: FontSize) => {
    setSubtitleFontSize(size);
    updateSettings({ subtitleFontSize: size });
  };

  const handleSubtitleBackgroundOpacityChange = (opacity: BackgroundOpacity) => {
    setSubtitleBackgroundOpacity(opacity);
    updateSettings({ subtitleBackgroundOpacity: opacity });
  };

  const handleSubtitleTextColorChange = (color: TextColor) => {
    setSubtitleTextColor(color);
    updateSettings({ subtitleTextColor: color });
  };

  const handleSubtitleFontFamilyChange = (family: FontFamily) => {
    setSubtitleFontFamily(family);
    updateSettings({ subtitleFontFamily: family });
  };

  const handleSubtitleTextEdgeChange = (edge: TextEdgeStyle) => {
    setSubtitleTextEdge(edge);
    updateSettings({ subtitleTextEdge: edge });
  };

  return (
    // Basic, self-contained demo that uses @lumi/player-core
    <div className="h-screen w-full">
      <div ref={containerRef} className="relative h-full w-full bg-black">
        <video
          ref={videoRef}
          className="w-full h-full bg-black object-contain"
          playsInline
          controls={false}
          crossOrigin="anonymous"
          data-custom-subtitles="true"
        >
          {/* Empty track for accessibility - subtitles are handled by CustomSubtitleOverlay */}
          <track kind="captions" label="Captions" srcLang="en" src="data:," />
        </video>

        <OverlayChrome
          containerRef={containerRef}
          videoRef={videoRef}
          isPlaying={isPlaying}
          onTogglePlay={onTogglePlay}
          time={time}
          duration={duration}
          onSeek={onSeek}
          levels={levels}
          selectedLevel={selectedLevel}
          onChangeLevel={onChangeLevel}
          captions={captions}
          activeCaption={activeCaption}
          onChangeCaption={onChangeCaption}
          playbackRate={playbackRate}
          onChangePlaybackRate={onChangePlaybackRate}
          subtitlePosition={subtitlePosition}
          onChangeSubtitlePosition={handleSubtitlePositionChange}
          subtitleFontSize={subtitleFontSize}
          onChangeSubtitleFontSize={handleSubtitleFontSizeChange}
          subtitleBackgroundOpacity={subtitleBackgroundOpacity}
          onChangeSubtitleBackgroundOpacity={handleSubtitleBackgroundOpacityChange}
          subtitleTextColor={subtitleTextColor}
          onChangeSubtitleTextColor={handleSubtitleTextColorChange}
          subtitleFontFamily={subtitleFontFamily}
          onChangeSubtitleFontFamily={handleSubtitleFontFamilyChange}
          subtitleTextEdge={subtitleTextEdge}
          onChangeSubtitleTextEdge={handleSubtitleTextEdgeChange}
          onBack={onBack}
          vttUrl={vttUrl}
          onControlsVisibilityChange={handleControlsVisibilityChange}
        />
      </div>
    </div>
  );
}
